import { createContext, useContext, useEffect, useMemo, useState } from 'react';

const MerchCartContext = createContext(null);

const STORAGE_KEY = 'merch_cart';

// Max purchasable units for a product/variant. stock 0 = unlimited (cap 20), negative = sold out.
export const maxPurchasable = (product, variant) => {
  const variants = product.variants || [];
  if (variants.length > 0) {
    const v = variants.find(x => x.label === variant);
    if (!v) return 0;
    const s = Number(v.stock || 0);
    return s === 0 ? 20 : Math.max(0, Math.min(20, s));
  }
  const s = Number(product.stock || 0);
  return s === 0 ? 20 : Math.max(0, Math.min(20, s));
};

export const MerchCartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || []; } catch { return []; }
  });
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = (product, variant, quantity = 1) => {
    const maxStock = maxPurchasable(product, variant);
    if (maxStock <= 0) return;
    setItems(prev => {
      const key = `${product.id}__${variant || ''}`;
      const existing = prev.find(i => i.key === key);
      if (existing) {
        return prev.map(i => i.key === key ? { ...i, max_stock: maxStock, quantity: Math.min(maxStock, i.quantity + quantity) } : i);
      }
      return [...prev, {
        key,
        product_id: product.id,
        name: product.name,
        image: (product.images || [])[0] || null,
        price: product.price,
        variant: variant || null,
        variant_label: product.variant_label || null,
        delivery_options: product.delivery_options || 'both',
        shipping_cost: product.shipping_cost || 0,
        max_stock: maxStock,
        quantity: Math.min(maxStock, quantity),
      }];
    });
    setIsOpen(true);
  };

  const updateQty = (key, quantity) => {
    setItems(prev => prev.map(i => i.key === key ? { ...i, quantity: Math.max(1, Math.min(i.max_stock || 20, quantity)) } : i));
  };
  const removeItem = (key) => setItems(prev => prev.filter(i => i.key !== key));
  const clearCart = () => setItems([]);

  const count = useMemo(() => items.reduce((a, i) => a + i.quantity, 0), [items]);
  const itemsTotal = useMemo(() => items.reduce((a, i) => a + i.price * i.quantity, 0), [items]);

  return (
    <MerchCartContext.Provider value={{ items, count, itemsTotal, addItem, updateQty, removeItem, clearCart, isOpen, openCart: () => setIsOpen(true), closeCart: () => setIsOpen(false) }}>
      {children}
    </MerchCartContext.Provider>
  );
};

export const useMerchCart = () => useContext(MerchCartContext);
